import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Elements } from "@stripe/react-stripe-js"
import { Row, Col } from "react-bootstrap"

import {
    ADD, DARK,
    IDLE, PENDING,
    PAYMENT_CURRENCY,
    PAYMENT_UPGRADE,
    PAYMENT_DOWNGRADE,
    PAYMENT_BUY,
} from "const"
import { MtCart, MtPaymentIntent } from "server/models"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_BILLING,
    StoreType,
    StripePaymentIntent,
    StripePaymentIntentData,

    LoadCartData,
    RestoreCartData,
    DeleteCartData,
    DeleteAllCartData,

    GetMtPaymentIntent,
    SaveMtPaymentIntent,
    DeleteMtPaymentIntent,


    GetStripePaymentIntent,
    EditStripePaymentIntent,
    CancelStripePaymentIntent,

    ActivePackageDaysLeft,
    setBillingCart,
    setBillingReloadCart,
} from "client/store"
import {
    CrossIcon,
    Loader,
    CartIcon,
    CloseIcon,
    SpinnerIcon,
    UndoIcon,
    Button,
} from "client/components"

import CheckoutForm from "client/pages/mt/billing/checkoutForm"
import {
    BillingModalWrapper,
    CloseButton,
    BillingModalContentWrapper,
    BillingModalTitle,
} from "client/pages/mt/billing/styled"
import {
    CartWrapper,
    CartIconWrapper,
    CartNumber,

    EmptyCartWrapper,
    EmptyCartIcon,
    EmptyCartText,

    CartItemRow,
    CartItemInfo,
    CartItemTitle,
    CartItemWarning,
    CartItemButton,
    CartPrice,
    CartTotal,
    CartTotalPrice,
    CartCheckoutButtonWrapper,
} from "./styled"

const Cart = () => {
    const stripePromise = useSelector((state: StoreType) => state.billing.stripePromise)
    const cart = useSelector((state: StoreType) => state.billing.cart)
    const reloadCart = useSelector((state: StoreType) => state.billing.reloadCart)

    const systemActivePackage = useSelector((state: StoreType) => state.system.packageActive)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const theme = useSelector((state: StoreType) => state.system.theme)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [inProgressId, setInProgressId] = useState<number>(null)
    const [removed, setRemoved] = useState<number[]>([])
    const [paymentIntent, setPaymentIntent] = useState<StripePaymentIntent>(null)

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        if (!reloadCart) return
        setLoadState(PENDING)
        LoadCartData(userData.accountId, userData.userId)
        .then((res) => {
            dispatch(setBillingCart(res))
            setLoadState(IDLE)
        })
    }, [reloadCart])

    const onDelete = (cartId: number) => {
        if (inProgressId) return
        setInProgressId(cartId)
        DeleteCartData(cartId, userData.accountId, userData.userId).then((res) => {
            if (res > 0) {
                const removedCopy = removed.slice()
                removedCopy.push(cartId)
                setRemoved(removedCopy)
            }
            setInProgressId(null)
        })
    }

    const onRestore = (cartRow: MtCart) => {
        if (inProgressId) return
        setInProgressId(cartRow.id)
        RestoreCartData(cartRow).then((res) => {
            if (res && res.id === cartRow.id) {
                const removedCopy = removed.filter((id) => id !== cartRow.id)
                setRemoved(removedCopy)
            }
            setInProgressId(null)
        })
    }

    const onCheckout = () => {
        if (inProgressId) return
        setLoadState(PENDING)

        // load existing payment intent on DB level
        GetMtPaymentIntent(userData.accountId, userData.userId)
        .then((accountIntent) => {
            // update or create payment intent on Stripe level
            const stripeIntentData: StripePaymentIntentData = {
                paymentIntentId: accountIntent ? accountIntent.paymentIntentId : null,
                accountId: userData.accountId,
                userId: userData.userId,
                userName: userData.userName,
                userEmail: userData.userEmail,
            }
            EditStripePaymentIntent(stripeIntentData).then((stripeIntentId) => {
                const newAccountIntent: MtPaymentIntent = {
                    accountId: userData.accountId,
                    userId: userData.userId,
                    paymentIntentId: stripeIntentId,
                }
                Promise.all([
                    SaveMtPaymentIntent(newAccountIntent), // save payment intent to DB
                    GetStripePaymentIntent(stripeIntentId) // load payment intent details from Stripe
                    .then((res) => setPaymentIntent(res)),
                ]).then(() => {
                    dispatch(setBillingReloadCart(false))
                    setTimeout(() => {
                        dispatch(setBillingReloadCart(true))
                    }, 0)
                })
            })
        })
    }

    const cancelPaymentIntent = () => {
        setLoadState(PENDING)
        CancelStripePaymentIntent(paymentIntent.id).then(() => (
            Promise.all([
                DeleteMtPaymentIntent(userData.accountId, userData.userId, paymentIntent.id),
                DeleteAllCartData(userData.accountId, userData.userId),
            ]).then(() => {
                setLoadState(IDLE)
                setPaymentIntent(null)
                setShowModal(false)

                dispatch(setBillingReloadCart(false))
                setTimeout(() => {
                    dispatch(setBillingReloadCart(true))
                }, 0)
            })
        ))
    }

    let totalPrice = 0
    const daysLeft = ActivePackageDaysLeft(systemActivePackage.dateEnd)

    const addAccess = hasAccess(PRM_BILLING, ADD)
    if (!addAccess) return null
    return (
        <CartWrapper>
            <CartIconWrapper onClick={() => setShowModal(true)}>
                <CartIcon />
                {
                    cart.length > 0
                    && <CartNumber>{cart.length}</CartNumber>
                }
            </CartIconWrapper>

            <BillingModalWrapper show={showModal}>
                <CloseButton onClick={() => setShowModal(false)}>
                    <CrossIcon />
                </CloseButton>
                <BillingModalContentWrapper>
                    {
                        loadState === PENDING
                        && <Loader paddingTop={150} />
                    }

                    <BillingModalTitle>
                        {paymentIntent ? lang.checkout : lang.cart}
                    </BillingModalTitle>

                    {
                        !cart.length && (
                            <EmptyCartWrapper>
                                <EmptyCartIcon>
                                    <CartIcon />
                                </EmptyCartIcon>
                                <EmptyCartText>{lang.cartEmpty}</EmptyCartText>
                            </EmptyCartWrapper>
                        )
                    }

                    {
                        cart.map((row) => {
                            const {
                                packageFrom,
                                packageTo,
                                packageAction,
                                librarySet,
                                calculatedPrice,
                            } = row

                            const isRemoved = removed.includes(row.id)
                            if (!isRemoved) totalPrice += calculatedPrice

                            let action: string
                            if (packageAction === PAYMENT_UPGRADE) action = lang.packageUpgraded
                            if (packageAction === PAYMENT_DOWNGRADE) action = lang.packageDowngraded

                            return (
                                <CartItemRow key={row.id} removed={isRemoved}>
                                    {
                                        packageFrom && packageTo && packageAction && (
                                            <CartItemInfo>
                                                <Row>
                                                    <Col xs={5}>
                                                        <CartItemTitle>{packageTo.name}</CartItemTitle>
                                                        <div>{`1 - ${packageTo.maxUsers} ${lang.users}`}</div>
                                                        <div>{lang.packageDuration}</div>
                                                    </Col>
                                                    {
                                                        packageAction !== PAYMENT_BUY && (
                                                            <Col xs={7}>
                                                                <CartItemTitle>{`${action} ${packageFrom.name}`}</CartItemTitle>
                                                                <div>{`${lang.daysLeft} ${daysLeft}`}</div>
                                                                {
                                                                    daysLeft === 0
                                                                    && <CartItemWarning>{lang.daysLeftWarning}</CartItemWarning>
                                                                }
                                                            </Col>
                                                        )
                                                    }
                                                </Row>
                                            </CartItemInfo>
                                        )
                                    }
                                    {
                                        librarySet && (
                                            <CartItemInfo>
                                                <CartItemTitle>{librarySet.name}</CartItemTitle>
                                                <div>{librarySet.publicId}</div>
                                            </CartItemInfo>
                                        )
                                    }

                                    <CartPrice>{`${calculatedPrice} ${PAYMENT_CURRENCY}`}</CartPrice>
                                    {
                                        !paymentIntent && ( // don't show delete button on checkout
                                            <CartItemButton onClick={() => (isRemoved ? onRestore(row) : onDelete(row.id))}>
                                                {
                                                    inProgressId === row.id
                                                        ? <SpinnerIcon />
                                                        : isRemoved ? <UndoIcon /> : <CloseIcon />
                                                }
                                            </CartItemButton>
                                        )
                                    }
                                </CartItemRow>
                            )
                        })
                    }
                    {
                        cart.length > 0 && (
                            <CartTotal>
                                {`${lang.total}:`}
                                <CartTotalPrice>
                                    {`${paymentIntent ? paymentIntent.amount : totalPrice} ${PAYMENT_CURRENCY}`}
                                </CartTotalPrice>
                            </CartTotal>
                        )
                    }
                    {
                        cart.length > 0
                        && !paymentIntent
                        && (
                            <CartCheckoutButtonWrapper>
                                <Button variant="success" onClick={onCheckout}>
                                    {lang.proceedToCheckout}
                                </Button>
                            </CartCheckoutButtonWrapper>
                        )
                    }
                    {
                        stripePromise && paymentIntent && paymentIntent.clientSecret && (
                            <Elements
                                options={{
                                    clientSecret: paymentIntent.clientSecret,
                                    appearance: theme === DARK ? {
                                        theme: "night",
                                        variables: {
                                            colorPrimary: "#20af66",
                                            colorBackground: "#25292e",
                                        },
                                    } : {
                                        theme: "stripe",
                                    },
                                }}
                                stripe={stripePromise}
                            >
                                <CheckoutForm
                                    totalPrice={totalPrice}
                                    onBack={() => setPaymentIntent(null)}
                                    onCancel={cancelPaymentIntent}
                                />
                            </Elements>
                        )
                    }
                </BillingModalContentWrapper>
            </BillingModalWrapper>
        </CartWrapper>
    )
}
export default Cart
